import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import api from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  ArrowLeft,
  Save,
  Loader2,
  Users,
  Clock,
  Plus,
  Trash2,
  FileDown,
  CheckCircle,
  Circle,
  Search,
  Filter,
  CalendarIcon,
  Link as LinkIcon,
  Copy,
  Building2
} from 'lucide-react';
import { formatDateDisplay } from '../../utils/dateUtils';
import { CloneConferenceDialog, AddByDepartmentDialog } from '../../components/BulkOperationsDialogs';

const ConferenceSetDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [conferenceSet, setConferenceSet] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [scheduleSets, setScheduleSets] = useState([]);
  const [exportSets, setExportSets] = useState([]);
  const [schoolLevels, setSchoolLevels] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [teacherDialogOpen, setTeacherDialogOpen] = useState(false);
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const initialLoadRef = useRef(true);
  
  // Filter states
  const [teacherSearch, setTeacherSearch] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');

  // Bulk operation dialog states
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [addByDeptDialogOpen, setAddByDeptDialogOpen] = useState(false);

  // Schedule Set Dialog states
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleDialogTab, setScheduleDialogTab] = useState('existing');
  const [scheduleFormLoading, setScheduleFormLoading] = useState(false);
  const [scheduleDate, setScheduleDate] = useState(new Date());
  const [newSchedule, setNewSchedule] = useState({
    friendly_name: '',
    conference_date: format(new Date(), 'yyyy-MM-dd'),
    school_level_id: 0,
    attendance_option: 'In-person Only',
    start_time: '08:00',
    end_time: '17:00',
    duration_minutes: 15,
    break_minutes: 5
  });
  const [selectedExistingSchedule, setSelectedExistingSchedule] = useState(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  // Auto-save when conference set settings change
  useEffect(() => {
    // Skip auto-save on initial load
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      return;
    }
    
    if (!conferenceSet || !hasUnsavedChanges) return;
    
    const timeoutId = setTimeout(() => {
      autoSave();
    }, 1000); // Debounce for 1 second
    
    return () => clearTimeout(timeoutId);
  }, [conferenceSet?.friendly_name, conferenceSet?.school_level_id, conferenceSet?.display_conference_url_in_virtual, conferenceSet?.display_friendly_url_in_other, conferenceSet?.notes]);

  const autoSave = async () => {
    if (!conferenceSet) return;
    
    try {
      await api.patch(`/conference-sets/${id}`, {
        friendly_name: conferenceSet.friendly_name,
        school_level_id: conferenceSet.school_level_id,
        display_conference_url_in_virtual: conferenceSet.display_conference_url_in_virtual,
        display_friendly_url_in_other: conferenceSet.display_friendly_url_in_other,
        notes: conferenceSet.notes
      });
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Auto-save failed:', error);
    }
  };

  // Update conference set with change tracking
  const updateConferenceSet = (updates) => {
    setConferenceSet(prev => ({ ...prev, ...updates }));
    setHasUnsavedChanges(true);
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [csRes, teachersRes, schedRes, exportsRes, userRes, deptRes] = await Promise.all([
        api.get(`/conference-sets/${id}`),
        api.get('/teachers'),
        api.get('/schedule-sets'),
        api.get('/export-sets'),
        api.get('/auth/me'),
        api.get('/departments')
      ]);
      
      const levelsRes = await api.get(`/schools/${userRes.data.school_id}/levels`);
      
      setConferenceSet(csRes.data);
      setTeachers(teachersRes.data);
      setScheduleSets(schedRes.data);
      setExportSets(exportsRes.data.filter(e => e.conference_set_id === id));
      setSchoolLevels(levelsRes.data);
      
      // Extract unique departments from teachers
      const uniqueDepts = [...new Set(teachersRes.data.map(t => t.department).filter(Boolean))];
      setDepartments(uniqueDepts);
    } catch (error) {
      toast.error('Failed to fetch data');
      navigate('/conference-sets');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/conference-sets/${id}`, {
        friendly_name: conferenceSet.friendly_name,
        school_level_id: conferenceSet.school_level_id,
        display_conference_url_in_virtual: conferenceSet.display_conference_url_in_virtual,
        display_friendly_url_in_other: conferenceSet.display_friendly_url_in_other,
        notes: conferenceSet.notes
      });
      setHasUnsavedChanges(false);
      toast.success('Saved');
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleAddTeachers = async () => {
    if (selectedTeachers.length === 0) {
      toast.error('Select at least one teacher');
      return;
    }

    try {
      await api.post(`/conference-sets/${id}/teachers`, selectedTeachers);
      toast.success(`Added ${selectedTeachers.length} teachers`);
      setTeacherDialogOpen(false);
      setSelectedTeachers([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to add teachers');
    }
  };

  const handleRemoveTeacher = async (teacherId) => {
    try {
      await api.delete(`/conference-sets/${id}/teachers/${teacherId}`);
      toast.success('Teacher removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove teacher');
    }
  };

  const handleApplySchedule = async (scheduleId) => {
    try {
      await api.post(`/conference-sets/${id}/link-schedule/${scheduleId}`);
      toast.success('Schedule linked!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link schedule');
    }
  };

  const handleUnlinkSchedule = async (scheduleId) => {
    try {
      await api.post(`/conference-sets/${id}/unlink-schedule/${scheduleId}`);
      toast.success('Schedule unlinked');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlink schedule');
    }
  };

  const handleGenerateExportSet = async () => {
    try {
      const response = await api.post(`/conference-sets/${id}/generate-export`);
      toast.success('Export set generated!');
      fetchData();
      // Navigate to the new export set
      navigate(`/export-sets/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate export set');
    }
  };

  // Schedule Dialog Handlers
  const handleScheduleDateSelect = (selectedDate) => {
    if (selectedDate) {
      setScheduleDate(selectedDate);
      setNewSchedule({ ...newSchedule, conference_date: format(selectedDate, 'yyyy-MM-dd') });
    }
  };

  const handleCreateAndApplySchedule = async () => {
    if (!newSchedule.friendly_name) {
      toast.error('Please enter a name for the schedule set');
      return;
    }

    setScheduleFormLoading(true);
    try {
      // Create new schedule set
      const createRes = await api.post('/schedule-sets', newSchedule);
      const newSchedId = createRes.data.id;
      
      // Link it to this conference set
      await api.post(`/conference-sets/${id}/link-schedule/${newSchedId}`);
      
      toast.success('Schedule set created and linked!');
      setScheduleDialogOpen(false);
      resetScheduleForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create/link schedule');
    } finally {
      setScheduleFormLoading(false);
    }
  };

  const handleApplyExistingSchedule = async () => {
    if (!selectedExistingSchedule) {
      toast.error('Please select a schedule set');
      return;
    }

    setScheduleFormLoading(true);
    try {
      await api.post(`/conference-sets/${id}/link-schedule/${selectedExistingSchedule}`);
      toast.success('Schedule linked!');
      setScheduleDialogOpen(false);
      setSelectedExistingSchedule(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link schedule');
    } finally {
      setScheduleFormLoading(false);
    }
  };

  const resetScheduleForm = () => {
    setNewSchedule({
      friendly_name: '',
      conference_date: format(new Date(), 'yyyy-MM-dd'),
      school_level_id: conferenceSet?.school_level_id || 0,
      attendance_option: 'In-person Only',
      start_time: '08:00',
      end_time: '17:00',
      duration_minutes: 15,
      break_minutes: 5
    });
    setScheduleDate(new Date());
    setSelectedExistingSchedule(null);
    setScheduleDialogTab('existing');
  };

  const openScheduleDialog = () => {
    // Pre-fill school level from conference set
    setNewSchedule(prev => ({
      ...prev,
      school_level_id: conferenceSet?.school_level_id || 0
    }));
    setScheduleDialogOpen(true);
  };

  const attendanceOptions = ['In-person Only', 'Remote Only', 'Attendee Choice'];

  // Get available teachers with filtering
  const availableTeachers = teachers.filter(t => {
    // Exclude teachers that are marked as excluded
    if (t.is_excluded) return false;
    
    // Exclude teachers already in the conference set
    if (conferenceSet?.teacher_ids?.includes(t.id)) return false;
    
    // Search filter
    if (teacherSearch) {
      const searchLower = teacherSearch.toLowerCase();
      const fullName = `${t.preferred_name || t.first_name} ${t.last_name}`.toLowerCase();
      if (!fullName.includes(searchLower)) return false;
    }
    
    // Department filter
    if (departmentFilter !== 'all' && t.department !== departmentFilter) return false;
    
    return true;
  });

  const handleSelectAll = () => {
    const allIds = availableTeachers.map(t => t.id);
    setSelectedTeachers(allIds);
  };

  const handleDeselectAll = () => {
    setSelectedTeachers([]);
  };

  const assignedTeachers = teachers.filter(t => conferenceSet?.teacher_ids?.includes(t.id));
  
  // Get linked schedules from conference set's schedule_set_ids array
  const linkedScheduleIds = conferenceSet?.schedule_set_ids || [];
  const linkedSchedules = scheduleSets.filter(s => linkedScheduleIds.includes(s.id));
  // Available schedules are those not already linked to THIS conference set (but can be linked to others)
  const availableSchedules = scheduleSets.filter(s => !linkedScheduleIds.includes(s.id));

  // Calculate step completion
  const step1Complete = conferenceSet?.friendly_name && conferenceSet?.school_level_id !== undefined;
  const step2Complete = assignedTeachers.length > 0;
  const step3Complete = linkedSchedules.length > 0;
  const step4Complete = exportSets.length > 0;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="conference-set-detail">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/conference-sets')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="font-outfit text-2xl font-bold text-slate-900">{conferenceSet?.friendly_name}</h1>
              <p className="text-slate-500">{conferenceSet?.school_level_description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              onClick={() => setCloneDialogOpen(true)}
              data-testid="clone-conference-btn"
            >
              <Copy className="h-4 w-4 mr-2" />
              Clone
            </Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </div>

        {/* Progress Steps */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {step1Complete ? (
                  <CheckCircle className="h-8 w-8 text-emerald-500" />
                ) : (
                  <Circle className="h-8 w-8 text-slate-300" />
                )}
                <div>
                  <p className="font-medium text-slate-900">Step 1: Settings</p>
                  <p className="text-sm text-slate-500">Configure conference set</p>
                </div>
              </div>
              <div className="h-px bg-slate-200 flex-1 mx-4" />
              <div className="flex items-center gap-3">
                {step2Complete ? (
                  <CheckCircle className="h-8 w-8 text-emerald-500" />
                ) : (
                  <Circle className="h-8 w-8 text-slate-300" />
                )}
                <div>
                  <p className="font-medium text-slate-900">Step 2: Teachers</p>
                  <p className="text-sm text-slate-500">{assignedTeachers.length} selected</p>
                </div>
              </div>
              <div className="h-px bg-slate-200 flex-1 mx-4" />
              <div className="flex items-center gap-3">
                {step3Complete ? (
                  <CheckCircle className="h-8 w-8 text-emerald-500" />
                ) : (
                  <Circle className="h-8 w-8 text-slate-300" />
                )}
                <div>
                  <p className="font-medium text-slate-900">Step 3: Schedule</p>
                  <p className="text-sm text-slate-500">{linkedSchedules.length} applied</p>
                </div>
              </div>
              <div className="h-px bg-slate-200 flex-1 mx-4" />
              <div className="flex items-center gap-3">
                {step4Complete ? (
                  <CheckCircle className="h-8 w-8 text-emerald-500" />
                ) : (
                  <Circle className="h-8 w-8 text-slate-300" />
                )}
                <div>
                  <p className="font-medium text-slate-900">Step 4: Export</p>
                  <p className="text-sm text-slate-500">{exportSets.length} ready</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 1: Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="font-outfit flex items-center gap-2">
              <span className="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">1</span>
              Conference Set Settings
            </CardTitle>
            <CardDescription>Configure the basic settings for this conference set</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Friendly Name</Label>
                <Input
                  value={conferenceSet?.friendly_name || ''}
                  onChange={(e) => updateConferenceSet({ friendly_name: e.target.value })}
                  data-testid="conf-set-name-edit"
                />
              </div>
              <div className="space-y-2">
                <Label>School Level</Label>
                <Select
                  value={String(conferenceSet?.school_level_id || 0)}
                  onValueChange={(v) => updateConferenceSet({ school_level_id: parseInt(v) })}
                >
                  <SelectTrigger data-testid="conf-set-level-edit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {schoolLevels.map((level) => (
                      <SelectItem key={level.level_id} value={String(level.level_id)}>
                        {level.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-3">
              <Label className="text-base font-medium">URL Display Options</Label>
              <p className="text-sm text-slate-500 mb-2">Choose what parents will see when they click their appointment link (only one can be selected)</p>
              <div 
                className={`flex items-start gap-4 p-4 rounded-lg cursor-pointer transition-colors ${
                  conferenceSet?.display_conference_url_in_virtual ? 'bg-indigo-50 border-2 border-indigo-300' : 'bg-slate-50 border-2 border-transparent hover:border-slate-200'
                }`}
                onClick={() => updateConferenceSet({ 
                  display_conference_url_in_virtual: true, 
                  display_friendly_url_in_other: false 
                })}
              >
                <div className={`w-5 h-5 mt-1 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  conferenceSet?.display_conference_url_in_virtual ? 'border-indigo-600 bg-indigo-600' : 'border-slate-300'
                }`}>
                  {conferenceSet?.display_conference_url_in_virtual && (
                    <div className="w-2 h-2 rounded-full bg-white" />
                  )}
                </div>
                <div className="flex-1">
                  <p className="font-medium mb-2">Direct Join Meeting Link</p>
                  <img 
                    src="/assets/join_meeting.png" 
                    alt="Direct Join Meeting Link example" 
                    className="rounded-md border border-slate-200 max-w-full"
                  />
                </div>
              </div>
              <div 
                className={`flex items-start gap-4 p-4 rounded-lg cursor-pointer transition-colors ${
                  conferenceSet?.display_friendly_url_in_other ? 'bg-indigo-50 border-2 border-indigo-300' : 'bg-slate-50 border-2 border-transparent hover:border-slate-200'
                }`}
                onClick={() => updateConferenceSet({ 
                  display_conference_url_in_virtual: false, 
                  display_friendly_url_in_other: true 
                })}
              >
                <div className={`w-5 h-5 mt-1 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  conferenceSet?.display_friendly_url_in_other ? 'border-indigo-600 bg-indigo-600' : 'border-slate-300'
                }`}>
                  {conferenceSet?.display_friendly_url_in_other && (
                    <div className="w-2 h-2 rounded-full bg-white" />
                  )}
                </div>
                <div className="flex-1">
                  <p className="font-medium mb-2">Friendly Link with Password</p>
                  <img 
                    src="/assets/friendly_link_w_password.png" 
                    alt="Friendly Link with Password example" 
                    className="rounded-md border border-slate-200 max-w-full"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={conferenceSet?.notes || ''}
                onChange={(e) => updateConferenceSet({ notes: e.target.value })}
              />
            </div>
          </CardContent>
        </Card>

        {/* Step 2: Teachers */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-outfit flex items-center gap-2">
                <span className="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">2</span>
                Select Teachers
              </CardTitle>
              <CardDescription>Choose which teachers will participate in this conference set</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setAddByDeptDialogOpen(true)} data-testid="add-by-dept-btn">
                <Building2 className="h-4 w-4 mr-2" />
                Add by Department
              </Button>
              <Button onClick={() => setTeacherDialogOpen(true)} data-testid="add-teachers-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Teachers
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {assignedTeachers.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-lg">
                <Users className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No teachers assigned yet</p>
                <Button className="mt-4" variant="outline" onClick={() => setTeacherDialogOpen(true)}>
                  Add Teachers
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Conference URL</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {assignedTeachers.map((teacher) => (
                    <TableRow key={teacher.id}>
                      <TableCell className="font-medium">
                        {teacher.preferred_name || teacher.first_name} {teacher.last_name}
                      </TableCell>
                      <TableCell>{teacher.department || <span className="text-slate-400">-</span>}</TableCell>
                      <TableCell>
                        {teacher.online_conference_url ? (
                          <Badge variant="default">Set</Badge>
                        ) : (
                          <Badge variant="outline">Not Set</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-rose-600"
                          onClick={() => handleRemoveTeacher(teacher.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Step 3: Schedule */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-outfit flex items-center gap-2">
                <span className="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">3</span>
                Link Schedule Sets
              </CardTitle>
              <CardDescription>Link schedule sets to define conference time slots</CardDescription>
            </div>
            <Button 
              onClick={openScheduleDialog}
              disabled={assignedTeachers.length === 0}
              data-testid="add-schedule-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Link Schedule
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Linked Schedules */}
            {linkedSchedules.length > 0 ? (
              <div>
                <h4 className="font-medium text-slate-900 mb-2">Linked Schedules</h4>
                <div className="space-y-2">
                  {linkedSchedules.map((sched) => (
                    <div key={sched.id} className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <div>
                        <p className="font-medium text-emerald-900">{sched.friendly_name}</p>
                        <p className="text-sm text-emerald-700">
                          {formatDateDisplay(sched.conference_date)} · {sched.start_time} - {sched.end_time} · {sched.time_slots?.filter(s => s.is_active).length} slots
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="default" className="bg-emerald-600">Linked</Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                          onClick={() => handleUnlinkSchedule(sched.id)}
                          data-testid={`unlink-schedule-${sched.id}`}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Unlink
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-lg">
                <Clock className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No schedules linked yet</p>
                <Button 
                  className="mt-4" 
                  variant="outline" 
                  onClick={openScheduleDialog}
                  disabled={assignedTeachers.length === 0}
                >
                  Link Schedule
                </Button>
              </div>
            )}
            {assignedTeachers.length === 0 && (
              <p className="text-sm text-amber-600">Add teachers first before linking a schedule</p>
            )}
          </CardContent>
        </Card>

        {/* Step 4: Export Sets */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-outfit flex items-center gap-2">
                <span className="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">4</span>
                Export Sets
              </CardTitle>
              <CardDescription>Generate and review export sets combining all linked schedules</CardDescription>
            </div>
            <Button 
              className="bg-indigo-600 hover:bg-indigo-700"
              onClick={handleGenerateExportSet}
              disabled={linkedSchedules.length === 0 || assignedTeachers.length === 0}
              data-testid="generate-export-btn"
            >
              <FileDown className="h-4 w-4 mr-2" />
              Generate Export Set
            </Button>
          </CardHeader>
          <CardContent>
            {linkedSchedules.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-lg">
                <FileDown className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Link at least one schedule to generate an export set</p>
              </div>
            ) : exportSets.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-lg">
                <FileDown className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No export sets yet</p>
                <p className="text-sm text-slate-400 mt-1">
                  Click "Generate Export Set" to create one with all {linkedSchedules.length} linked schedule{linkedSchedules.length > 1 ? 's' : ''}
                </p>
                <Button 
                  className="mt-4 bg-indigo-600 hover:bg-indigo-700"
                  onClick={handleGenerateExportSet}
                  data-testid="generate-export-btn-inline"
                >
                  <FileDown className="h-4 w-4 mr-2" />
                  Generate Export Set
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Active Slots</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {exportSets.map((es) => (
                    <TableRow key={es.id}>
                      <TableCell className="font-medium">{es.friendly_name}</TableCell>
                      <TableCell>{es.slots?.filter(s => s.is_active).length || 0}</TableCell>
                      <TableCell>{formatDateDisplay(es.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/export-sets/${es.id}`)}
                        >
                          Manage & Export
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Add Teachers Dialog */}
        <Dialog open={teacherDialogOpen} onOpenChange={setTeacherDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>Add Teachers to Conference Set</DialogTitle>
              <DialogDescription>Filter and select teachers to add</DialogDescription>
            </DialogHeader>
            
            {/* Filters */}
            <div className="grid grid-cols-2 gap-3 py-4 border-b">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by name..."
                  value={teacherSearch}
                  onChange={(e) => setTeacherSearch(e.target.value)}
                  className="pl-9"
                  data-testid="teacher-search-input"
                />
              </div>
              <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                <SelectTrigger data-testid="department-filter">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments.map((dept) => (
                    <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Bulk Actions */}
            <div className="flex items-center justify-between py-2">
              <p className="text-sm text-slate-500">
                {availableTeachers.length} teachers match filters · {selectedTeachers.length} selected
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleSelectAll} data-testid="select-all-btn">
                  Select All ({availableTeachers.length})
                </Button>
                <Button variant="outline" size="sm" onClick={handleDeselectAll} data-testid="deselect-all-btn">
                  Deselect All
                </Button>
              </div>
            </div>

            {/* Teacher List */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {availableTeachers.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No teachers match the current filters</p>
              ) : (
                <div className="space-y-1">
                  {availableTeachers.map((teacher) => (
                    <div 
                      key={teacher.id}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedTeachers.includes(teacher.id) ? 'bg-indigo-50 border border-indigo-200' : 'hover:bg-slate-50'
                      }`}
                      onClick={() => {
                        if (selectedTeachers.includes(teacher.id)) {
                          setSelectedTeachers(selectedTeachers.filter(id => id !== teacher.id));
                        } else {
                          setSelectedTeachers([...selectedTeachers, teacher.id]);
                        }
                      }}
                    >
                      <Checkbox
                        checked={selectedTeachers.includes(teacher.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedTeachers([...selectedTeachers, teacher.id]);
                          } else {
                            setSelectedTeachers(selectedTeachers.filter(id => id !== teacher.id));
                          }
                        }}
                      />
                      <div className="flex-1">
                        <p className="font-medium">{teacher.preferred_name || teacher.first_name} {teacher.last_name}</p>
                        <p className="text-sm text-slate-500">
                          {teacher.department || 'No department'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter className="border-t pt-4">
              <Button variant="outline" onClick={() => setTeacherDialogOpen(false)}>Cancel</Button>
              <Button
                className="bg-indigo-600 hover:bg-indigo-700"
                onClick={handleAddTeachers}
                disabled={selectedTeachers.length === 0}
                data-testid="submit-add-teachers"
              >
                Add {selectedTeachers.length} Teacher(s)
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add/Link Schedule Dialog */}
        <Dialog open={scheduleDialogOpen} onOpenChange={(open) => {
          setScheduleDialogOpen(open);
          if (!open) resetScheduleForm();
        }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>Add Schedule Set</DialogTitle>
              <DialogDescription>Link an existing schedule set or create a new one</DialogDescription>
            </DialogHeader>

            <Tabs value={scheduleDialogTab} onValueChange={setScheduleDialogTab} className="flex-1 flex flex-col min-h-0">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="existing" data-testid="tab-existing-schedule">
                  <LinkIcon className="h-4 w-4 mr-2" />
                  Link Existing
                </TabsTrigger>
                <TabsTrigger value="new" data-testid="tab-new-schedule">
                  <Plus className="h-4 w-4 mr-2" />
                  Create New
                </TabsTrigger>
              </TabsList>

              {/* Link Existing Tab */}
              <TabsContent value="existing" className="flex-1 overflow-auto mt-4">
                {availableSchedules.length === 0 ? (
                  <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-lg">
                    <Clock className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No available schedule sets</p>
                    <p className="text-sm text-slate-400 mt-1">Switch to "Create New" tab to make one</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {availableSchedules.map((sched) => (
                      <div 
                        key={sched.id}
                        className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-colors border-2 ${
                          selectedExistingSchedule === sched.id 
                            ? 'bg-indigo-50 border-indigo-300' 
                            : 'bg-slate-50 border-transparent hover:border-slate-200'
                        }`}
                        onClick={() => setSelectedExistingSchedule(sched.id)}
                        data-testid={`select-schedule-${sched.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                            selectedExistingSchedule === sched.id ? 'border-indigo-600 bg-indigo-600' : 'border-slate-300'
                          }`}>
                            {selectedExistingSchedule === sched.id && (
                              <div className="w-2 h-2 rounded-full bg-white" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium">{sched.friendly_name}</p>
                            <p className="text-sm text-slate-500">
                              {formatDateDisplay(sched.conference_date)} · {sched.start_time} - {sched.end_time} · {sched.time_slots?.filter(s => s.is_active).length || 0} slots
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Create New Tab */}
              <TabsContent value="new" className="flex-1 overflow-auto mt-4">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Friendly Name *</Label>
                    <Input
                      value={newSchedule.friendly_name}
                      onChange={(e) => setNewSchedule({ ...newSchedule, friendly_name: e.target.value })}
                      placeholder="Morning Sessions - Oct 15"
                      data-testid="new-sched-name-input"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Conference Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start" data-testid="new-sched-date-picker">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {formatDateDisplay(newSchedule.conference_date)}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={scheduleDate}
                            onSelect={handleScheduleDateSelect}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="space-y-2">
                      <Label>School Level</Label>
                      <Select
                        value={String(newSchedule.school_level_id)}
                        onValueChange={(v) => setNewSchedule({ ...newSchedule, school_level_id: parseInt(v) })}
                      >
                        <SelectTrigger data-testid="new-sched-level-select">
                          <SelectValue placeholder="Select level" />
                        </SelectTrigger>
                        <SelectContent>
                          {schoolLevels.map((level) => (
                            <SelectItem key={level.level_id} value={String(level.level_id)}>
                              {level.description}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Default Attendee Option</Label>
                    <Select
                      value={newSchedule.attendance_option}
                      onValueChange={(v) => setNewSchedule({ ...newSchedule, attendance_option: v })}
                    >
                      <SelectTrigger data-testid="new-sched-attendance-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {attendanceOptions.map((opt) => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-slate-500">
                      You will be able to make one-off changes for particular teachers during the review of the Export Set that is created.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Start Time</Label>
                      <Input
                        type="time"
                        value={newSchedule.start_time}
                        onChange={(e) => setNewSchedule({ ...newSchedule, start_time: e.target.value })}
                        data-testid="new-sched-start-time"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>End Time</Label>
                      <Input
                        type="time"
                        value={newSchedule.end_time}
                        onChange={(e) => setNewSchedule({ ...newSchedule, end_time: e.target.value })}
                        data-testid="new-sched-end-time"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Duration (minutes)</Label>
                      <Input
                        type="number"
                        value={newSchedule.duration_minutes}
                        onChange={(e) => setNewSchedule({ ...newSchedule, duration_minutes: parseInt(e.target.value) })}
                        min={5}
                        max={60}
                        data-testid="new-sched-duration"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Break Between (minutes)</Label>
                      <Input
                        type="number"
                        value={newSchedule.break_minutes}
                        onChange={(e) => setNewSchedule({ ...newSchedule, break_minutes: parseInt(e.target.value) })}
                        min={0}
                        max={30}
                        data-testid="new-sched-break"
                      />
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter className="border-t pt-4 mt-4">
              <Button variant="outline" onClick={() => setScheduleDialogOpen(false)}>Cancel</Button>
              {scheduleDialogTab === 'existing' ? (
                <Button
                  className="bg-indigo-600 hover:bg-indigo-700"
                  onClick={handleApplyExistingSchedule}
                  disabled={!selectedExistingSchedule || scheduleFormLoading}
                  data-testid="submit-apply-schedule"
                >
                  {scheduleFormLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <LinkIcon className="h-4 w-4 mr-2" />}
                  Link Schedule
                </Button>
              ) : (
                <Button
                  className="bg-indigo-600 hover:bg-indigo-700"
                  onClick={handleCreateAndApplySchedule}
                  disabled={!newSchedule.friendly_name || scheduleFormLoading}
                  data-testid="submit-create-schedule"
                >
                  {scheduleFormLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                  Create & Link
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Clone Conference Dialog */}
        <CloneConferenceDialog
          open={cloneDialogOpen}
          onOpenChange={setCloneDialogOpen}
          conferenceSet={conferenceSet}
          onSuccess={(newId) => {
            navigate(`/conference-sets/${newId}`);
          }}
        />

        {/* Add by Department Dialog */}
        <AddByDepartmentDialog
          open={addByDeptDialogOpen}
          onOpenChange={setAddByDeptDialogOpen}
          conferenceSetId={id}
          departments={departments}
          onSuccess={() => {
            fetchData();
          }}
        />
      </div>
    </DashboardLayout>
  );
};

export default ConferenceSetDetailPage;
